import React, { useRef, useEffect } from 'react';
import Radar from '../../../../../../customModules/radar';

function FindStoreAutocomplete({ radarKey, onSelect, setSearch, setStores, setLoading, setError, selectedStore }) {
  const autocompleteRef = useRef(null);
  const autoCompleteResultRef = useRef([]);

  // Helper: fetch stores by coordinates
  const fetchStoresByLocation = async (latitude, longitude) => {
    try {
      const result = await Radar.searchGeofences({
        tags: ['store'],
        limit: 20,
        near: { latitude, longitude },
      });
      return result.geofences.filter((store) => {
        // WWW-22297 filter out clearance stores by checking if externalId starts with '05'
        return !store.externalId.startsWith('05');
      });
    } catch {
      throw new Error('Could not fetch stores.');
    }
  };

  // Helper: try to get stores by device location with timeout, fallback to IP geocode
  const fetchInitialStores = async (selectedStore) => {
    try {
      if (selectedStore) {
        const location = await Radar.forwardGeocode({ query: selectedStore.metadata.zipcode });
        const { latitude, longitude } = location.addresses[0] || {};
        return await fetchStoresByLocation(latitude, longitude);
      }

      const locationPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Location timeout')), 5000);
        Radar.getLocation()
          .then((location) => {
            clearTimeout(timeout);
            resolve(location);
          })
          .catch((err) => {
            clearTimeout(timeout);
            reject(err);
          });
      });

      const result = await locationPromise.catch(async () => {
        const ipGeo = await Radar.ipGeocode();
        const { latitude, longitude } = ipGeo.address || {};
        return latitude && longitude ? { latitude, longitude } : null;
      });

      if (result && result.latitude && result.longitude) {
        console.log('Location found:', result.latitude, result.longitude);
        return await fetchStoresByLocation(result.latitude, result.longitude);
      }

      throw new Error('Could not fetch stores.');
    } catch (err) {
      console.error(err.message || 'Error fetching initial stores');
      throw new Error('Could not fetch stores.');
    }
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const handleKeyUp = (event) => {
      // Find all autocomplete input fields
      const inputFields = document.querySelectorAll('.radar-autocomplete-input');
      if (
        event.target.classList.contains('radar-autocomplete-input') &&
        event.key === 'Enter' &&
        autoCompleteResultRef.current.length > 0
      ) {
        // Simulate selecting the first result
        const address = autoCompleteResultRef.current[0];
        setSearch(address.formattedAddress || '');
        setLoading(true);
        (async () => {
          try {
            const stores = await fetchStoresByLocation(address.latitude, address.longitude);
            if (!isMounted) return;
            setStores(stores);
            setLoading(false);
            onSelect && onSelect(address);
          } catch (err) {
            if (!isMounted) return;
            setError(err.message);
            setLoading(false);
          }
        })();
        // Blur input fields
        inputFields.forEach((input) => {
          input.setAttribute('aria-expanded', 'false');
          input.blur();
        });
        autoCompleteResultRef.current = [];
      }
    };

    const initializeAutocomplete = async () => {
      try {
        Radar.initialize(radarKey, {
          locationTimeout: 5000,
          desiredAccuracy: false,
        });
        autocompleteRef.current = Radar.ui.autocomplete({
          container: 'autocomplete',
          width: '100%',
          placeholder: 'Enter zip code, city, or state',
          countryCode: 'US',
          onResults: (results) => {
            autoCompleteResultRef.current = results;
          },
          onSelection: async (address) => {
            setSearch(address.formattedAddress || '');
            setLoading(true);
            try {
              const stores = await fetchStoresByLocation(address.latitude, address.longitude);
              if (!isMounted) return;
              setStores(stores);
              setLoading(false);
              onSelect && onSelect(address);
            } catch (err) {
              if (!isMounted) return;
              setError(err.message);
              setLoading(false);
            }
          },
        });

        // Initial fetch for stores
        try {
          const stores = await fetchInitialStores(selectedStore);
          if (!isMounted) return;
          setStores(stores);
          setLoading(false);
        } catch (err) {
          if (!isMounted) return;
          setError(err.message);
          setLoading(false);
        }
      } catch {
        if (!isMounted) return;
        setError('Could not initialize store search.');
        setLoading(false);
      }
    };

    initializeAutocomplete();
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      isMounted = false;
      autocompleteRef.current?.remove && autocompleteRef.current.remove();
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [radarKey, setLoading, setError, setSearch, setStores, onSelect]);

  return <div id='autocomplete' className='mb-3' />;
}

export default FindStoreAutocomplete;
